import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams, Link } from "react-router-dom";
import {
  Facebook, Instagram, Twitter, Youtube, Music2, AtSign,
  CheckCircle2, AlertCircle, Loader2, ExternalLink, Unplug, Send, Activity,
} from "lucide-react";
import { toast } from "sonner";

import PageHeader from "@/components/PageHeader";
import SetupHelper from "@/components/SetupHelper";
import { PlatformIcon } from "@/components/PlatformIcon";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { api } from "@/lib/api";
import { appConfirm } from "@/components/AppDialogHost";

const brandIcon = (platform) => {
  const B = (props) => <PlatformIcon platform={platform} {...props} />;
  return B;
};

const PLATFORM_META = {
  facebook:  { label: "Facebook Page",      Icon: Facebook,  color: "#1877F2", needs: "Meta app" },
  instagram: { label: "Instagram Business", Icon: Instagram, color: "#E1306C", needs: "Meta app" },
  threads:   { label: "Threads (Meta)",     Icon: AtSign,    color: "#F5F1EA", needs: "Threads app" },
  x:         { label: "X (Twitter)",        Icon: Twitter,   color: "#0EA5E9", needs: "X OAuth 2.0 app" },
  tiktok:    { label: "TikTok",             Icon: Music2,    color: "#FF0050", needs: "TikTok app" },
  youtube:   { label: "YouTube",            Icon: Youtube,   color: "#FF0000", needs: "Google OAuth client" },
  linkedin:  { label: "LinkedIn",           Icon: brandIcon("linkedin"), color: "#0A66C2", needs: "LinkedIn app" },
  discord:   { label: "Discord",            Icon: brandIcon("discord"),  color: "#5865F2", needs: "" },
  telegram:  { label: "Telegram",           Icon: brandIcon("telegram"), color: "#26A5E4", needs: "" },
  bluesky:   { label: "Bluesky",            Icon: brandIcon("bluesky"),  color: "#0285FF", needs: "" },
  vimeo:     { label: "Vimeo",              Icon: brandIcon("vimeo"),    color: "#1AB7EA", needs: "" },
  pinterest: { label: "Pinterest",          Icon: brandIcon("pinterest"), color: "#E60023", needs: "Pinterest app" },
};

// Platforms connected by pasting credentials (no OAuth redirect)
const CREDENTIAL_PLATFORMS = {
  discord: {
    title: "Connect Discord",
    help: "In Discord: your server → Server Settings → Integrations → Webhooks → New Webhook → pick the channel to post in → Copy Webhook URL.",
    fields: [{ key: "webhook_url", label: "Webhook URL", placeholder: "https://discord.com/api/webhooks/…" }],
  },
  telegram: {
    title: "Connect Telegram",
    help: "1) Message @BotFather → /newbot → copy the token. 2) Add your bot to the channel/group as an admin. 3) Chat ID: @yourchannel for public channels, or the numeric ID (e.g. -1001234567890) for groups.",
    fields: [
      { key: "bot_token", label: "Bot token", placeholder: "123456:ABC-DEF…", type: "password" },
      { key: "chat_id", label: "Chat ID", placeholder: "@mychannel or -1001234567890" },
    ],
  },
  bluesky: {
    title: "Connect Bluesky",
    help: "On bsky.app: Settings → Privacy & Security → App Passwords → Add App Password. Never paste your main account password.",
    fields: [
      { key: "handle", label: "Handle", placeholder: "station.bsky.social" },
      { key: "app_password", label: "App password", placeholder: "xxxx-xxxx-xxxx-xxxx", type: "password" },
    ],
  },
  vimeo: {
    title: "Connect Vimeo",
    help: "developer.vimeo.com → My Apps → Create an app → Generate an access token: pick \"Authenticated (you)\" and check the private, upload and edit scopes. Note: free Vimeo accounts must request upload access for the app; paid plans have it instantly.",
    fields: [{ key: "access_token", label: "Access token", placeholder: "vimeo personal access token", type: "password" }],
  },
};

// Which login endpoint to call per platform (Facebook + Instagram share Meta)
const LOGIN_ENDPOINT = {
  facebook: "/social/meta/login",
  instagram: "/social/meta/login",
  threads: "/social/threads/login",
  x: "/social/x/login",
  tiktok: "/social/tiktok/login",
  youtube: "/social/youtube/login",
  linkedin: "/social/li/login",
  pinterest: "/social/pin/login",
};
// Aliases keep blocked brand words out of XHR URLs (ad blockers filter them)
const apiSlug = (p) => (p === "linkedin" ? "li" : p === "pinterest" ? "pin" : p);
// Maps each platform to the backend "configured" key
const CONFIG_KEY = {
  facebook: "meta", instagram: "meta", threads: "threads",
  x: "x", tiktok: "tiktok", youtube: "youtube",
  discord: "discord", telegram: "telegram", bluesky: "bluesky", vimeo: "vimeo",
  linkedin: "linkedin", pinterest: "pinterest",
};

export default function SocialConnections() {
  const [items, setItems] = useState([]);
  const [configured, setConfigured] = useState({});
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState(null);
  const [params, setParams] = useSearchParams();
  const [pages, setPages] = useState(null);
  const [credPlatform, setCredPlatform] = useState(null);
  const [credForm, setCredForm] = useState({});
  const [pinBoards, setPinBoards] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/social/status");
      setItems(r.data.platforms || []);
      setConfigured(r.data.configured || {});
    } catch {
      toast.error("Failed to load social connections");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Pinterest board list — loaded once the connection shows up
  useEffect(() => {
    const pin = items.find((i) => i.platform === "pinterest");
    if (pin?.connected && !pinBoards) {
      api.get("/social/pin/boards").then((r) => setPinBoards(r.data)).catch(() => {});
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items]);

  const setPinBoard = async (board_id) => {
    const b = (pinBoards?.boards || []).find((x) => x.id === board_id);
    try {
      await api.post("/social/pin/board", { board_id, board_name: b?.name || "" });
      toast.success(`Pins will go to "${b?.name || "board"}"`);
      setPinBoards((prev) => ({ ...prev, selected: board_id }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't set board");
    }
  };

  // Surface callback redirects (e.g. ?platform=meta&step=pick_page or ?error=...)
  useEffect(() => {
    const platform = params.get("platform");
    const err = params.get("error");
    const errDesc = params.get("error_description") || params.get("error_reason");
    const step = params.get("step");
    const connected = params.get("connected");
    if (err) {
      const msg = errDesc ? `${err} — ${errDesc}` : err;
      // Persist for diagnostics — many providers throw the user back with this
      // and the toast disappears in 4s. Log to console so devtools keeps it.
      console.error(`[social-oauth] ${platform || "platform"} callback error:`, { error: err, description: errDesc });
      toast.error(`${platform || "Platform"} error: ${msg}`, { duration: 10000 });
    }
    if (connected) toast.success(`${platform || "Platform"} connected.`);
    if (step === "pick_page" && platform === "meta") {
      (async () => {
        try {
          const r = await api.get("/social/meta/pages");
          setPages(r.data.pages || []);
        } catch (e) {
          toast.error("Could not load Facebook Pages");
        }
      })();
    }
    if (platform || err || connected || step) {
      // clear querystring for clean URL
      setParams({}, { replace: true });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const connect = async (platform) => {
    if (CREDENTIAL_PLATFORMS[platform]) {
      setCredForm({});
      setCredPlatform(platform);
      return;
    }
    const ck = CONFIG_KEY[platform];
    if (!configured[ck]) {
      toast.error("This platform's developer app credentials aren't set on the server yet. Contact admin.");
      return;
    }
    setWorking(platform);
    try {
      const r = await api.post(LOGIN_ENDPOINT[platform]);
      const url = r?.data?.authorize_url;
      if (!url) {
        console.error(`[social-oauth] ${platform} login returned no authorize_url`, r?.data);
        toast.error(`${platform}: server did not return an OAuth URL. Open browser devtools → Console for details.`);
        setWorking(null);
        return;
      }
      console.info(`[social-oauth] redirecting to ${platform}:`, url);
      window.location.href = url;
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      const networkMsg = e?.message;
      // Most useful failure modes:
      //   - 400 "Create a station first" (no station_id on the user)
      //   - 401 "Not authenticated" (session expired)
      //   - 503 OAuth not configured (env keys missing — should already be caught by `configured`)
      //   - network error (browser blocked, offline, CORS, etc.)
      console.error(`[social-oauth] ${platform} login failed:`, { status, detail, networkMsg, error: e });
      let msg = detail;
      if (!msg && status === 401) msg = "Your session expired — please log in again.";
      if (!msg && status === 400) msg = "Create a station first before connecting social accounts.";
      if (!msg && status === 503) msg = "Server-side OAuth app credentials are missing — contact admin.";
      if (!msg) msg = `Could not start OAuth${status ? ` (HTTP ${status})` : ""}${networkMsg ? `: ${networkMsg}` : ""}. Open browser devtools → Console for details.`;
      toast.error(msg, { duration: 8000 });
      setWorking(null);
    }
  };

  const submitCred = async () => {
    const spec = CREDENTIAL_PLATFORMS[credPlatform];
    for (const f of spec.fields) {
      if (!(credForm[f.key] || "").trim()) { toast.error(`${f.label} is required`); return; }
    }
    setWorking(credPlatform);
    try {
      const r = await api.post(`/social/${credPlatform}/connect`, credForm);
      toast.success(`Connected: ${r.data.account_label || PLATFORM_META[credPlatform]?.label}`);
      setCredPlatform(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Connection failed", { duration: 10000 });
    } finally { setWorking(null); }
  };

  const setYtPrivacy = async (privacy) => {
    try {
      await api.post("/social/youtube/privacy", { privacy });
      toast.success(`YouTube uploads will now be ${privacy}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't update visibility");
    }
  };

  const disconnect = async (platform) => {
    if (!await appConfirm(`Disconnect ${PLATFORM_META[platform]?.label}?`)) return;
    try {
      await api.post(`/social/${apiSlug(platform)}/disconnect`);
      toast.success("Disconnected");
      load();
    } catch { toast.error("Failed to disconnect"); }
  };

  const testPost = async (platform) => {
    // Some platforms require media (image or video) and will fail with a
    // media-required error if we send a text-only probe. Warn the user up
    // front so the failure message makes sense.
    const mediaOnly = { instagram: "Instagram", tiktok: "TikTok", youtube: "YouTube" };
    if (mediaOnly[platform]) {
      const ok = await appConfirm(
        `${mediaOnly[platform]} requires media (image/video). A text-only probe will fail with a media-required error. Continue anyway?`
      );
      if (!ok) return;
    }
    setWorking(platform);
    try {
      const r = await api.post(`/social/${apiSlug(platform)}/test-post`);
      const url = r?.data?.external_url;
      const id = r?.data?.external_id || "";
      const stamp = r?.data?.stamp || "";
      console.info(`[social-test-post] ${platform} OK`, r?.data);
      toast.success(
        `${PLATFORM_META[platform]?.label || platform}: probe published ${stamp ? `at ${stamp}` : ""}${id ? ` (id ${id.slice(0, 24)}${id.length > 24 ? "…" : ""})` : ""}`,
        {
          duration: 12000,
          action: url ? { label: "View →", onClick: () => window.open(url, "_blank", "noopener,noreferrer") } : undefined,
        }
      );
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      console.error(`[social-test-post] ${platform} failed:`, { status, detail, error: e });
      toast.error(
        `${PLATFORM_META[platform]?.label || platform} test failed: ${detail || e?.message || "unknown error"}`,
        { duration: 12000 }
      );
    } finally {
      setWorking(null);
    }
  };

  const selectPage = async (page_id) => {
    setWorking("meta-select");
    try {
      const r = await api.post("/social/meta/select-page", {
        page_id, enable_facebook: true, enable_instagram: true,
      });
      toast.success(`Connected: Facebook${r.data.instagram ? " + Instagram" : ""}`);
      setPages(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not connect Page");
    } finally { setWorking(null); }
  };

  const itemByPlatform = (p) => items.find((i) => i.platform === p) || { platform: p, connected: false };

  return (
    <div className="p-6 md:p-10 max-w-6xl mx-auto animate-fade-up" data-testid="social-connections-page">
      <PageHeader
        eyebrow="// SOCIAL CONNECTIONS"
        title="Connect your social accounts."
        description="Each platform uses its own OAuth flow. Once connected, scheduled posts will publish automatically via the dispatcher."
      />

      <div className="mb-4 flex justify-end">
        <Link
          to="/dashboard/social-connections/audit"
          data-testid="social-audit-link"
          className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-md border border-line bg-bg-surface hover:border-brand-blue hover:text-brand-blue transition"
        >
          <Activity className="h-3.5 w-3.5" /> View Test Post audit log →
        </Link>
      </div>

      <SetupHelper />

      <Dialog open={!!credPlatform} onOpenChange={(o) => { if (!o) setCredPlatform(null); }}>
        <DialogContent className="max-w-md" data-testid="cred-connect-dialog">
          {credPlatform && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <PlatformIcon platform={credPlatform} className="h-5 w-5" style={{ color: PLATFORM_META[credPlatform]?.color }} />
                  {CREDENTIAL_PLATFORMS[credPlatform].title}
                </DialogTitle>
                <DialogDescription className="text-xs leading-relaxed">
                  {CREDENTIAL_PLATFORMS[credPlatform].help}
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-3">
                {CREDENTIAL_PLATFORMS[credPlatform].fields.map((f) => (
                  <div key={f.key}>
                    <label className="text-xs text-ink-muted block mb-1">{f.label}</label>
                    <input
                      type={f.type || "text"}
                      value={credForm[f.key] || ""}
                      onChange={(e) => setCredForm((prev) => ({ ...prev, [f.key]: e.target.value }))}
                      placeholder={f.placeholder}
                      data-testid={`cred-field-${f.key}`}
                      className="w-full rounded-md border border-line bg-bg-elevated px-3 py-2 text-sm font-mono focus:border-brand-amber focus:outline-none"
                      autoComplete="off"
                    />
                  </div>
                ))}
                <button
                  onClick={submitCred}
                  disabled={working === credPlatform}
                  data-testid="cred-connect-submit"
                  className="w-full inline-flex items-center justify-center gap-2 rounded-md bg-brand-amber text-bg px-4 py-2 text-sm font-bold hover:bg-brand-amber/90 transition disabled:opacity-50"
                >
                  {working === credPlatform ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                  {working === credPlatform ? "Verifying…" : "Verify & Connect"}
                </button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {pages && (
        <div className="mb-6 rounded-xl border-2 border-brand-amber bg-brand-amber/10 p-5 animate-pulse-slow" data-testid="meta-pick-page">
          <div className="inline-flex items-center gap-2 mb-2 rounded-full bg-brand-amber/20 text-brand-amber px-3 py-1 text-[11px] font-mono uppercase tracking-wider">
            <span className="h-2 w-2 rounded-full bg-brand-amber animate-pulse"></span>
            Action Required — One More Click
          </div>
          <div className="font-display text-2xl font-bold mb-1">Pick the Page you want to publish to</div>
          <div className="text-sm text-ink-secondary mb-4">
            Meta is linked, but no Page is selected yet. The cards below will keep saying <span className="font-mono text-ink-primary">&ldquo;Not connected&rdquo;</span> until you click <span className="text-brand-amber font-semibold">&ldquo;Use this →&rdquo;</span> on the Page below. If your Instagram Business account is linked to that Page, we&rsquo;ll wire it up too.
          </div>
          <div className="space-y-2">
            {pages.length === 0 && (
              <div className="text-sm text-ink-muted italic">No Pages found. You must be an admin or editor of at least one Facebook Page.</div>
            )}
            {pages.map((p) => {
              const isWorking = working === "meta-select";
              const hasIG = !!p.instagram_business_id;
              return (
                <button
                  key={p.page_id}
                  disabled={isWorking}
                  onClick={() => selectPage(p.page_id)}
                  data-testid={`meta-page-${p.page_id}`}
                  className={`w-full text-left rounded-lg border-2 p-4 flex items-center justify-between transition group ${hasIG ? "border-brand-amber/60 bg-brand-amber/5" : "border-line bg-bg-elevated"} hover:border-brand-amber hover:bg-brand-amber/10 disabled:opacity-50`}
                >
                  <div>
                    <div className="font-display font-bold text-lg">{p.page_name}</div>
                    <div className="text-xs text-ink-muted font-mono">{p.page_id}</div>
                  </div>
                  <div className="flex items-center gap-3">
                    {hasIG && (
                      <span className="rounded-full bg-pink-500/15 text-pink-300 px-3 py-1 text-xs font-semibold inline-flex items-center gap-1">
                        <span className="h-1.5 w-1.5 rounded-full bg-pink-400"></span>
                        IG linked
                      </span>
                    )}
                    <span className="inline-flex items-center gap-1 rounded-md bg-brand-amber text-bg px-4 py-2 text-sm font-bold group-hover:bg-brand-amber/90 transition">
                      {isWorking ? "Connecting…" : "Use this →"}
                    </span>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {loading ? (
        <div className="text-center py-20 font-mono text-xs text-ink-muted">LOADING...</div>
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          {Object.keys(PLATFORM_META).map((platform) => {
            const meta = PLATFORM_META[platform];
            const it = itemByPlatform(platform);
            const ck = CONFIG_KEY[platform];
            const Icon = meta.Icon;
            const isWorking = working === platform;
            const adminConfigured = !!configured[ck];

            return (
              <div
                key={platform}
                data-testid={`social-card-${platform}`}
                className={`rounded-xl border bg-bg-surface p-5 ${it.connected ? "border-emerald-500/40" : "border-line"}`}
              >
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <Icon className="h-5 w-5" style={{ color: meta.color }} />
                    <span className="font-display font-bold">{meta.label}</span>
                  </div>
                  {it.connected ? (
                    <span className="inline-flex items-center gap-1 text-xs text-emerald-300">
                      <CheckCircle2 className="h-3.5 w-3.5" /> Connected
                    </span>
                  ) : adminConfigured ? (
                    <span className="text-xs text-ink-muted">Not connected</span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-xs text-brand-amber">
                      <AlertCircle className="h-3.5 w-3.5" /> Admin setup pending
                    </span>
                  )}
                </div>

                <div className="text-xs text-ink-muted mb-1">Account</div>
                <div className="text-sm font-mono mb-4">{it.account_label || "—"}</div>

                {platform === "youtube" && it.connected && (
                  <div className="mb-4">
                    <div className="text-xs text-ink-muted mb-1">Upload visibility</div>
                    <select
                      value={it.youtube_privacy || "private"}
                      onChange={(e) => setYtPrivacy(e.target.value)}
                      data-testid="youtube-privacy-select"
                      className="w-full rounded-md border border-line bg-bg-elevated px-2 py-1.5 text-xs font-mono focus:border-brand-amber focus:outline-none"
                    >
                      <option value="private">Private — only you can see uploads</option>
                      <option value="unlisted">Unlisted — anyone with the link</option>
                      <option value="public">Public — visible to everyone</option>
                    </select>
                    <div className="mt-1 text-[10px] text-ink-muted italic">
                      Note: until your Google app passes YouTube's API audit, YouTube locks API uploads to private regardless of this setting.
                    </div>
                  </div>
                )}

                {platform === "pinterest" && it.connected && (
                  <div className="mb-4">
                    <div className="text-xs text-ink-muted mb-1">Pin to board</div>
                    {pinBoards?.boards?.length ? (
                      <select
                        value={pinBoards.selected || ""}
                        onChange={(e) => setPinBoard(e.target.value)}
                        data-testid="pinterest-board-select"
                        className="w-full rounded-md border border-line bg-bg-elevated px-2 py-1.5 text-xs font-mono focus:border-brand-amber focus:outline-none"
                      >
                        {pinBoards.boards.map((b) => (
                          <option key={b.id} value={b.id}>{b.name}</option>
                        ))}
                      </select>
                    ) : (
                      <div className="text-[11px] text-ink-muted italic">
                        No boards found — create a board on Pinterest first, then reload this page.
                      </div>
                    )}
                  </div>
                )}

                <div className="flex items-center gap-2 flex-wrap">
                  {it.connected ? (
                    <>
                      <button
                        onClick={() => testPost(platform)}
                        disabled={isWorking}
                        data-testid={`social-test-${platform}`}
                        className="text-xs inline-flex items-center gap-1 rounded-md border border-brand-blue/40 bg-brand-blue/10 text-brand-blue px-3 py-1.5 hover:bg-brand-blue/20 transition disabled:opacity-50"
                        title="Publish a tiny dated probe so you know publishing actually works"
                      >
                        {isWorking ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
                        {isWorking ? "Testing…" : "Test Post"}
                      </button>
                      <button
                        onClick={() => disconnect(platform)}
                        data-testid={`social-disconnect-${platform}`}
                        className="text-xs inline-flex items-center gap-1 rounded-md border border-line bg-bg-elevated px-3 py-1.5 hover:text-brand-onair transition"
                      >
                        <Unplug className="h-3.5 w-3.5" /> Disconnect
                      </button>
                    </>
                  ) : (
                    <button
                      onClick={() => connect(platform)}
                      disabled={isWorking || !adminConfigured}
                      data-testid={`social-connect-${platform}`}
                      className="text-xs inline-flex items-center gap-1 rounded-md border border-brand-amber/40 bg-brand-amber/10 text-brand-amber px-3 py-1.5 hover:bg-brand-amber/20 transition disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isWorking ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ExternalLink className="h-3.5 w-3.5" />}
                      {isWorking ? "Redirecting..." : "Connect"}
                    </button>
                  )}
                </div>
                {!adminConfigured && (
                  <div className="mt-3 text-[11px] text-ink-muted italic">
                    Server admin must add {meta.needs} credentials to <span className="font-mono">.env</span>.
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <div className="mt-8 rounded-xl border border-line bg-bg-surface p-5 text-sm text-ink-secondary">
        <div className="font-display text-lg font-bold mb-2">How publishing works</div>
        <ul className="list-disc list-inside space-y-1 text-xs">
          <li>Schedule a post via the Calendar.</li>
          <li>At publish time, the background dispatcher picks it up.</li>
          <li>For each connected platform, the dispatcher calls the official API and updates the post's status to <span className="font-mono text-emerald-300">published</span> or <span className="font-mono text-brand-onair">failed</span> with an error message.</li>
          <li>TikTok and YouTube require video — image posts are auto-converted to a 5-second clip via ffmpeg.</li>
          <li>Instagram requires an image — text-only posts are skipped.</li>
          <li>Pinterest requires an image — pins go to the board you pick above.</li>
          <li>Discord, Telegram and Bluesky connect with a simple paste — no developer app or approval process needed.</li>
        </ul>
      </div>
    </div>
  );
}
